import { useMutation } from '@apollo/client/react';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import type { UpsertAdvisorAgreementResult } from '@src/components/sco/Advisors/types/advisorAgreement';
import { blankAgreementInput, fastAgreementHref } from '@src/components/sco/Advisors/utils/draftSeatAgreement';
import { cleanErrorMessage } from '@src/components/sco/Advisors/utils/sessionFormat';
import { UPSERT_ADVISOR_AGREEMENT } from '@src/gql/mutations/UpsertAdvisorAgreement';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';

/** Create an ad-hoc FAST draft (no seat required) and open the editor. */
export const useCreateAdvisorAgreement = () => {
  const paths = useAdvisorsPaths();
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);
  const [upsertAdvisorAgreement, upsertMutation] = useMutation<UpsertAdvisorAgreementResult>(UPSERT_ADVISOR_AGREEMENT);
  const create = useCallback(async () => {
    setError(null);
    try {
      const result = await upsertAdvisorAgreement({
        variables: {
          input: blankAgreementInput()
        }
      });
      const agreementId = result.data?.upsertAdvisorAgreement?.advisorAgreement?.advisorAgreementId || null;
      if (!agreementId) {
        setError('Agreement was created but no id was returned.');
        return null;
      }
      await router.push(fastAgreementHref(paths, agreementId));
      return agreementId;
    } catch (err) {
      setError(cleanErrorMessage(err, 'Could not create a new agreement.'));
      return null;
    }
  }, [paths, router, upsertAdvisorAgreement]);
  return {
    create,
    loading: upsertMutation.loading,
    error
  };
};